import type { TypedDocumentNode } from '@apollo/client';
import gql from 'graphql-tag';

import { CloudVpcFragment } from 'Admin/Clusters/CreateClusterDialog/useVpcs/fragments';

export type Subnet = {
  id: string;
  name: string;
  availabilityZone: string;
  cidr: string;
  private: boolean;
  delegated: boolean | null;
  resourceGroup: string | null;
  natGateway: string | null;
  secondaryIpRanges?: SecondaryIpRange[];
  // monkey patched during normalization, used as parent back ref; usefull when picking subnets in the case we need to pass vpc/resourceGroup info to gateway
  vpc?: string;
};

type SecondaryIpRange = {
  name: string;
  cidr: string;
};

export type CloudVpc = {
  id: string;
  name: string;
  cidr: string | null;
  frameManaged: boolean;
  subnets: Subnet[];
  dnsServers: string[];
  resourceGroup: string | null;
};

export const CloudVpcsQuery: TypedDocumentNode<CloudVpcsResponse, ResourceGroupsQueryVariables> = gql`
  query CloudVpcsQuery($cloudServiceId: ExternalId!, $datacenterId: ExternalId!) {
    cloudVpcs(cloudServiceId: $cloudServiceId, datacenterId: $datacenterId) {
      ...CloudVpc
    }
  }
  ${CloudVpcFragment}
`;

export type CloudVpcsResponse = {
  cloudVpcs: CloudVpc[];
};

export type ResourceGroupsQueryVariables = {
  cloudServiceId: string;
  datacenterId: string;
  subscriptionId?: string;
};

export const ResourceGroupsQuery: TypedDocumentNode<ResourceGroupsResponse, ResourceGroupsQueryVariables> = gql`
  query ResourceGroupsQuery($cloudServiceId: ExternalId!, $datacenterId: ExternalId!, $subscriptionId: String) {
    resourceGroups(cloudServiceId: $cloudServiceId, datacenterId: $datacenterId, subscriptionId: $subscriptionId) {
      resourceGroupName
    }
  }
`;

export type ResourceGroup = {
  resourceGroupName: string;
};

export type ResourceGroupsResponse = {
  resourceGroups: ResourceGroup[];
};

export type SubnetsQueryVariables = {
  cloudServiceId: string;
  datacenterId: string;
  az?: string;
  name?: string;
  resourceGroup?: string;
  delegated?: boolean;
};

export type SubnetsQueryResponse = {
  subnets: Subnet[];
};

export const SubnetsQuery: TypedDocumentNode<SubnetsQueryResponse, SubnetsQueryVariables> = gql`
  query SubnetsQuery(
    $cloudServiceId: ExternalId!
    $datacenterId: ExternalId!
    $az: String
    $name: String
    $resourceGroup: String
    $delegated: Boolean
  ) {
    subnets(
      cloudServiceId: $cloudServiceId
      datacenterId: $datacenterId
      az: $az
      name: $name
      resourceGroup: $resourceGroup
      delegated: $delegated
    ) {
      id
      uniqueId
      name
      availabilityZone: az
      cidr
      private
      delegated
      resourceGroup
      natGateway
    }
  }
`;
